/**
 * Trading Test Page
 * Advanced trading test page with price range selection, interactive chart,
 * and multiple order submission modes (random, curve-based, chart-based)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { ExchangeClient, InfoClient, toSatoshi } from "../sdk";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { useTradePrice } from "../hooks/useTradePrice";
import { DEFAULT_BASE_URL, DEFAULT_WS_URL } from "../configs/base";
import { PriceAmountChart } from "./trade_test_page/PriceAmountChart";
import { PriceRangeModal } from "./trade_test_page/PriceRangeModal";
import {
  TestSubmissionButton,
  ChartOrderSubmissionButton,
  ResetChartButton,
  LoadPendingOrdersButton,
  CancelAllOrdersButton,
} from "./trade_test_page/TradingTestPageButtons";
import "./trade_test_page/tradetest.css";

interface OrderPoint {
  price: number;
  amount: number;
}

interface ChartOrder {
  price: number;
  amount: number;
  id: string;
  side?: "buy" | "sell" | "long" | "short";
  orderId?: string; // For pending orders
  isPending?: boolean; // Flag to distinguish pending orders
}

export default function TradingTestPage() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [marketId, setMarketId] = useState<string>("");
  const [mnemonic, setMnemonic] = useState("");
  const [authToken, setAuthToken] = useState("");

  // Price range configuration
  const [minPrice, setMinPrice] = useState(10000); // 10,000 USDC
  const [maxPrice, setMaxPrice] = useState(100000); // 100,000 USDC
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  // Order generation mode
  const [orderMode, setOrderMode] = useState<"random" | "curve">("random");
  const [curveIntensity, setCurveIntensity] = useState(2); // Higher = more aggressive buying at lower prices

  // Long/Short tab selection
  const [orderSide, setOrderSide] = useState<"long" | "short">("long");

  // Chart orders (user-defined)
  const [chartOrders, setChartOrders] = useState<ChartOrder[]>([]);
  const [pendingOrders, setPendingOrders] = useState<ChartOrder[]>([]);

  // Test results
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Price range modal state
  const [isPriceRangeModalOpen, setIsPriceRangeModalOpen] = useState(false);
  const [roundingStep, setRoundingStep] = useState(100);

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);

  // Use markets hook
  const { markets, fetchMarkets, getSymbolByMarketId } = useMarkets({
    baseURL,
    sessionToken: "",
    autoFetchMarkets: true,
  });

  // Use onboarding hook
  const {
    mnemonic: onboardMnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    loading: onboardLoading,
    onboard: performOnboard,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Derive symbol from marketId for mark price subscription
  const marketSymbol = marketId ? getSymbolByMarketId(marketId) : null;
  const symbolForTradePrice = marketSymbol !== null ? marketSymbol : undefined;

  // Use trade price hook to get mark price
  // Note: walletAddress is optional - not required for public price subscriptions
  const { markPrice } = useTradePrice({
    baseURL,
    wsURL: DEFAULT_WS_URL,
    marketId: marketId || "",
    symbol: symbolForTradePrice,
    // walletAddress is optional - only needed for user-specific subscriptions
    // authToken is also optional - not required for public price data
  });

  // Track if user has manually changed prices (to prevent auto-update from overriding)
  const [hasManualPriceChange, setHasManualPriceChange] = useState(false);

  // Update min/max price based on mark price (only if user hasn't manually changed them)
  // mark_price is a string containing whole number (not satoshi), so parse it
  // Formula: max = 1.5 × markPrice (rounded to roundingStep), min = markPrice / 1.5 (rounded to roundingStep)
  useEffect(() => {
    if (markPrice?.mark_price && !hasManualPriceChange) {
      const markPriceValue = parseFloat(markPrice.mark_price);
      // Calculate and round to nearest roundingStep
      const calculatedMaxPrice =
        Math.round((markPriceValue * 1.5) / roundingStep) * roundingStep;
      const calculatedMinPrice =
        Math.round(markPriceValue / 1.5 / roundingStep) * roundingStep;
      setMinPrice(calculatedMinPrice);
      setMaxPrice(calculatedMaxPrice);
    }
  }, [markPrice?.mark_price, hasManualPriceChange, roundingStep]);

  // Sync onboard values
  useEffect(() => {
    if (onboardMnemonic) setMnemonic(onboardMnemonic);
    if (onboardAuthToken) setAuthToken(onboardAuthToken);
  }, [onboardMnemonic, onboardAuthToken]);

  // Compute default market ID
  const defaultMarketId =
    markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ?? markets[0].market_id) ||
        (markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          "")
      : null;

  useEffect(() => {
    if (defaultMarketId && !marketId) {
      setMarketId(defaultMarketId);
    }
  }, [defaultMarketId]);

  // Update refs
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  // Initialize ExchangeClient
  useEffect(() => {
    if (mnemonic && mnemonic.trim() !== "") {
      exchangeClientRef.current = new ExchangeClient({
        baseURL: baseURLRef.current,
        mnemonic,
      });
      exchangeClientRef.current.initialize().catch((error) => {
        console.error("Failed to initialize ExchangeClient:", error);
      });
    }
  }, [mnemonic, baseURL]);

  // Initialize InfoClient
  useEffect(() => {
    infoClientRef.current = new InfoClient({
      baseURL: baseURLRef.current,
    });
  }, [baseURL]);

  // Fetch current price
  useEffect(() => {
    if (!marketId || !infoClientRef.current) return;

    const fetchPrice = async () => {
      try {
        const ticker = await infoClientRef.current.getTicker(marketId);
        if (ticker?.lastPrice) {
          const price =
            typeof ticker.lastPrice === "string"
              ? parseFloat(ticker.lastPrice)
              : ticker.lastPrice;
          setCurrentPrice(price);
        }
      } catch (error) {
        console.error("Failed to fetch current price:", error);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, [marketId]);

  // Generate random order points
  const generateRandomOrders = useCallback(
    (count: number): OrderPoint[] => {
      const orders: OrderPoint[] = [];
      for (let i = 0; i < count; i++) {
        const price = minPrice + Math.random() * (maxPrice - minPrice);
        const amount = 0.001 + Math.random() * 0.01; // Random amount between 0.001 and 0.011 BTC
        orders.push({ price, amount });
      }
      return orders.sort((a, b) => a.price - b.price);
    },
    [minPrice, maxPrice],
  );

  // Generate curve-based order points (more orders at lower prices)
  const generateCurveOrders = useCallback(
    (count: number): OrderPoint[] => {
      const orders: OrderPoint[] = [];
      const priceRange = maxPrice - minPrice;

      for (let i = 0; i < count; i++) {
        // Use exponential curve to favor lower prices
        const normalized = Math.random();
        const curveValue = Math.pow(normalized, curveIntensity);
        const price = minPrice + curveValue * priceRange;

        // Amount increases as price decreases (buy more at lower prices)
        const priceRatio = (maxPrice - price) / priceRange;
        const amount = 0.001 + priceRatio * 0.02; // More amount at lower prices

        orders.push({ price, amount });
      }
      return orders.sort((a, b) => a.price - b.price);
    },
    [minPrice, maxPrice, curveIntensity],
  );

  // Submit test orders (random or curve-based)
  const handleTestSubmission = useCallback(async () => {
    if (!exchangeClientRef.current || !marketId || !walletAddress) {
      toast.error("Setup required", {
        description:
          "Please ensure mnemonic, market ID, and wallet address are configured.",
      });
      return;
    }

    setIsSubmitting(true);
    const results: any[] = [];

    try {
      const orderCount = 10; // Generate 10 test orders
      const orders =
        orderMode === "random"
          ? generateRandomOrders(orderCount)
          : generateCurveOrders(orderCount);

      toast.info("Submitting test orders", {
        description: `Submitting ${orders.length} ${orderMode} orders...`,
      });

      for (const order of orders) {
        try {
          const leverage = 1.0;
          const leverageStr = toSatoshi(leverage.toString());

          const orderResult = await exchangeClientRef.current.placeOrder({
            marketId,
            side: (orderSide === "long" ? "buy" : "sell") as const,
            orderType: "LIMIT" as const,
            price: order.price,
            quantity: order.amount,
            timeInForce: "GTC" as const,
            leverage: leverageStr,
          });

          results.push({
            success: true,
            order: {
              price: order.price,
              amount: order.amount,
              orderId: orderResult?.orderId || orderResult?.id,
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          results.push({
            success: false,
            order: {
              price: order.price,
              amount: order.amount,
            },
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Small delay between orders
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      setTestResults((prev) => [...prev, ...results]);

      const successCount = results.filter((r) => r.success).length;
      toast.success("Test orders submitted", {
        description: `${successCount}/${results.length} orders placed successfully.`,
      });
    } catch (error: any) {
      toast.error("Test submission failed", {
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    marketId,
    walletAddress,
    orderMode,
    generateRandomOrders,
    generateCurveOrders,
  ]);

  // Submit chart orders
  const handleChartOrderSubmission = useCallback(async () => {
    if (!exchangeClientRef.current || !marketId || !walletAddress) {
      toast.error("Setup required", {
        description:
          "Please ensure mnemonic, market ID, and wallet address are configured.",
      });
      return;
    }

    if (chartOrders.length === 0) {
      toast.warning("No chart orders", {
        description: "Please add orders to the chart first.",
      });
      return;
    }

    setIsSubmitting(true);
    const results: any[] = [];

    try {
      toast.info("Submitting chart orders", {
        description: `Submitting ${chartOrders.length} chart orders...`,
      });

      for (const chartOrder of chartOrders) {
        try {
          const leverage = 1.0;
          const leverageStr = toSatoshi(leverage.toString());

          const orderResult = await exchangeClientRef.current.placeOrder({
            marketId,
            side: (chartOrder.side === "short" || chartOrder.side === "sell"
              ? "sell"
              : "buy") as const,
            orderType: "LIMIT" as const,
            price: chartOrder.price,
            quantity: chartOrder.amount,
            timeInForce: "GTC" as const,
            leverage: leverageStr,
          });

          results.push({
            success: true,
            order: {
              price: chartOrder.price,
              amount: chartOrder.amount,
              orderId: orderResult?.orderId || orderResult?.id,
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          results.push({
            success: false,
            order: {
              price: chartOrder.price,
              amount: chartOrder.amount,
            },
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Small delay between orders
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      setTestResults((prev) => [...prev, ...results]);

      const successCount = results.filter((r) => r.success).length;
      toast.success("Chart orders submitted", {
        description: `${successCount}/${results.length} orders placed successfully.`,
      });
    } catch (error: any) {
      toast.error("Chart order submission failed", {
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [marketId, walletAddress, chartOrders]);

  // Reset chart
  const handleResetChart = useCallback(() => {
    setChartOrders([]);
    toast.info("Chart reset", {
      description: "All chart orders have been cleared.",
    });
  }, []);

  // Add order to chart
  const handleAddChartOrder = useCallback(
    (price: number, amount: number) => {
      const newOrder: ChartOrder = {
        price,
        amount,
        id: `chart-${Date.now()}-${Math.random()}`,
        side: orderSide,
      };
      setChartOrders((prev) => [...prev, newOrder]);
    },
    [orderSide],
  );

  // Remove order from chart
  const handleRemoveChartOrder = useCallback((id: string) => {
    setChartOrders((prev) => prev.filter((order) => order.id !== id));
    setPendingOrders((prev) => prev.filter((order) => order.id !== id));
  }, []);

  // Load pending orders
  const handleLoadPendingOrders = useCallback(async () => {
    if (!infoClientRef.current || !walletAddress || !marketId) {
      toast.error("Setup required", {
        description:
          "Please ensure wallet address and market ID are configured.",
      });
      return;
    }

    try {
      toast.info("Loading pending orders...");
      const ordersResponse =
        await infoClientRef.current.getOrders(walletAddress);
      const ordersArray = ordersResponse?.orders || [];
      console.log(ordersResponse);
      // Filter pending orders for current market
      const pending = ordersArray
        .filter((order: any) => {
          const orderMarketId =
            order.marketId || order.market_id || order.assetPair;
          const status = (order.status || "").toUpperCase();
          const pendingStatuses = [
            "ORDER_STATUS_PENDING",
            "ORDER_STATUS_OPEN",
            "PENDING",
            "OPEN",
            "NEW",
            "PARTIALLY_FILLED",
          ];
          return orderMarketId === marketId && pendingStatuses.includes(status);
        })
        .map((order: any) => {
          const side =
            order.side ||
            (order.direction === "LONG"
              ? "long"
              : order.direction === "SHORT"
                ? "short"
                : "buy");

          return {
            price: order.price,
            amount: order.quantity,
            id: `pending-${order.id || order.orderId || order.order_id}`,
            side: side === "buy" ? "long" : side === "sell" ? "short" : side,
            orderId: order.id || order.orderId || order.order_id,
            isPending: true,
          } as ChartOrder;
        });

      setPendingOrders(pending);
      toast.success("Pending orders loaded", {
        description: `Loaded ${pending.length} pending orders.`,
      });
    } catch (error: any) {
      toast.error("Failed to load pending orders", {
        description: error.message || "Unknown error occurred",
      });
    }
  }, [walletAddress, marketId]);

  // Cancel all orders
  const handleCancelAllOrders = useCallback(
    async (filter: "long" | "short" | "all") => {
      if (!exchangeClientRef.current || !marketId || !walletAddress) {
        toast.error("Setup required", {
          description:
            "Please ensure mnemonic, market ID, and wallet address are configured.",
        });
        return;
      }

      try {
        // Get pending orders
        const ordersResponse =
          await infoClientRef.current.getOrders(walletAddress);
        const ordersArray = ordersResponse?.orders || [];

        // Filter orders based on filter type
        const ordersToCancel = ordersArray.filter((order: any) => {
          const orderMarketId =
            order.marketId || order.market_id || order.assetPair;
          const status = (order.status || "").toUpperCase();
          const pendingStatuses = [
            "ORDER_STATUS_PENDING",
            "ORDER_STATUS_OPEN",
            "PENDING",
            "OPEN",
            "NEW",
            "PARTIALLY_FILLED",
          ];

          if (orderMarketId !== marketId || !pendingStatuses.includes(status)) {
            return false;
          }

          if (filter === "all") return true;

          const side =
            order.side ||
            (order.direction === "LONG"
              ? "long"
              : order.direction === "SHORT"
                ? "short"
                : "buy");
          if (filter === "long") {
            return side === "buy" || side === "long";
          }
          if (filter === "short") {
            return side === "sell" || side === "short";
          }
          return false;
        });

        if (ordersToCancel.length === 0) {
          toast.info("No orders to cancel", {
            description: `No ${filter} pending orders found.`,
          });
          return;
        }

        setIsSubmitting(true);
        toast.info("Canceling orders", {
          description: `Canceling ${ordersToCancel.length} ${filter} orders...`,
        });

        let successCount = 0;
        let failCount = 0;

        for (const order of ordersToCancel) {
          try {
            const orderId = order.id || order.orderId || order.order_id;
            await exchangeClientRef.current.cancelOrder({
              orderId,
              marketId,
            });
            successCount++;
          } catch (error: any) {
            failCount++;
            console.error("Failed to cancel order:", error);
          }
          // Small delay between cancellations
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Reload pending orders after cancellation
        await handleLoadPendingOrders();

        toast.success("Orders canceled", {
          description: `${successCount} orders canceled successfully${failCount > 0 ? `, ${failCount} failed` : ""}.`,
        });
      } catch (error: any) {
        toast.error("Failed to cancel orders", {
          description: error.message || "Unknown error occurred",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [marketId, walletAddress, handleLoadPendingOrders],
  );

  return (
    <div className="App-content-row">
      <h1>Trading Test Page</h1>
      <p>
        Advanced trading test with price range, interactive chart, and multiple
        submission modes
      </p>

      {/* Configuration Section */}
      <div className="App-section">
        <h2>Configuration</h2>
        <div className="trading-test-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="trading-test-input"
              />
            </label>
          </div>
          <div>
            <label>
              Market:
              <select
                value={marketId || ""}
                onChange={(e) => setMarketId(e.target.value)}
                className="trading-test-select"
              >
                {markets.length === 0 ? (
                  <option value="">Loading markets...</option>
                ) : (
                  <>
                    <option value="" disabled>
                      -- Select a market --
                    </option>
                    {markets.map((market) => {
                      const marketIdValue =
                        market.market_id ?? market.id?.toString() ?? "";
                      const displayName = market.moniker
                        ? market.moniker
                        : market.symbol
                          ? market.symbol
                          : `Market ID: ${marketIdValue}`;
                      return (
                        <option key={marketIdValue} value={marketIdValue}>
                          {displayName}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </label>
          </div>
          <div>
            <label>
              Mnemonic:
              <input
                type="text"
                value={mnemonic}
                onChange={(e) => {
                  setMnemonic(e.target.value);
                  setOnboardMnemonic(e.target.value);
                }}
                placeholder="Enter mnemonic"
                className="trading-test-input"
              />
            </label>
          </div>
          {currentPrice && (
            <div className="trading-test-price-display">
              <strong>Current Price:</strong> {currentPrice.toLocaleString()}{" "}
              USDC
            </div>
          )}
        </div>
      </div>

      {/* Long/Short Tabs */}
      <div className="trading-test-tabs">
        <button
          className={`trading-test-tab ${orderSide === "long" ? "active" : ""}`}
          onClick={() => setOrderSide("long")}
        >
          Long (Buy)
        </button>
        <button
          className={`trading-test-tab ${orderSide === "short" ? "active" : ""}`}
          onClick={() => setOrderSide("short")}
        >
          Short (Sell)
        </button>
      </div>

      {/* Interactive Chart */}
      <div className="App-section">
        <h2>Interactive Price-Amount Chart</h2>
        <div className="trading-test-chart-controls">
          <div className="trading-test-chart-info">
            <span>Orders on chart: {chartOrders.length}</span>
            <span
              onClick={() => setIsPriceRangeModalOpen(true)}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              title="Click to configure price range"
            >
              Price range: {minPrice.toLocaleString()} -{" "}
              {maxPrice.toLocaleString()} USDC
            </span>
          </div>
        </div>
        <PriceAmountChart
          minPrice={minPrice}
          maxPrice={maxPrice}
          orders={chartOrders}
          pendingOrders={pendingOrders}
          onAddOrder={handleAddChartOrder}
          onRemoveOrder={handleRemoveChartOrder}
          currentPrice={currentPrice}
          markPrice={
            markPrice?.mark_price ? parseFloat(markPrice.mark_price) : null
          }
        />
        <div className="price-amount-chart-instructions">
          <p>
            Click on the chart to add orders. Click on existing orders to remove
            them.
          </p>
          <p>Y-axis: Price (USDC) | X-axis: Amount (BTC)</p>
        </div>
      </div>

      {/* Submission Buttons */}
      <div className="App-section">
        <h2>Order Submission</h2>
        <div className="trading-test-submission-buttons">
          <ResetChartButton
            onClick={handleResetChart}
            disabled={chartOrders.length === 0}
          />
          <TestSubmissionButton
            onClick={handleTestSubmission}
            disabled={isSubmitting || !marketId || !walletAddress}
            orderMode={orderMode}
          />
          <ChartOrderSubmissionButton
            onClick={handleChartOrderSubmission}
            disabled={
              isSubmitting ||
              chartOrders.length === 0 ||
              !marketId ||
              !walletAddress
            }
            orderCount={chartOrders.length}
          />
        </div>
        <h2>Pending Orders Management</h2>
        <div className="trading-test-pending-orders-controls">
          <LoadPendingOrdersButton
            onClick={handleLoadPendingOrders}
            disabled={!marketId || !walletAddress}
            pendingCount={pendingOrders.length}
          />
          <div className="trading-test-cancel-buttons">
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("long")}
              disabled={isSubmitting || !marketId || !walletAddress}
              filter="long"
            />
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("short")}
              disabled={isSubmitting || !marketId || !walletAddress}
              filter="short"
            />
            <CancelAllOrdersButton
              onClick={() => handleCancelAllOrders("all")}
              disabled={isSubmitting || !marketId || !walletAddress}
              filter="all"
            />
          </div>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="trading-test-results">
          <h2>Test Results</h2>
          <div className="trading-test-results-summary">
            <div>
              Total: {testResults.length} | Success:{" "}
              {testResults.filter((r) => r.success).length} | Failed:{" "}
              {testResults.filter((r) => !r.success).length}
            </div>
            <button
              onClick={() => setTestResults([])}
              className="trading-test-button-secondary"
            >
              Clear Results
            </button>
          </div>
          <div className="trading-test-results-list">
            {testResults.slice(-20).map((result, index) => (
              <div
                key={index}
                className={`trading-test-result-item ${
                  result.success ? "success" : "error"
                }`}
              >
                <div>
                  <strong>
                    {result.success ? "✓" : "✗"} Order @{" "}
                    {result.order.price.toFixed(2)} USDC
                  </strong>
                  <span>Amount: {result.order.amount.toFixed(4)} BTC</span>
                  {result.order.orderId && (
                    <span>
                      Order ID: {result.order.orderId.substring(0, 16)}...
                    </span>
                  )}
                  {result.error && (
                    <span className="error-text">{result.error}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Range Configuration Modal */}
      <PriceRangeModal
        isOpen={isPriceRangeModalOpen}
        onClose={() => setIsPriceRangeModalOpen(false)}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onMinPriceChange={(price) => {
          setMinPrice(price);
          setHasManualPriceChange(true);
        }}
        onMaxPriceChange={(price) => {
          setMaxPrice(price);
          setHasManualPriceChange(true);
        }}
        markPrice={markPrice}
        hasManualPriceChange={hasManualPriceChange}
        onResetToAuto={() => {
          if (markPrice?.mark_price) {
            setHasManualPriceChange(false);
            const markPriceValue = parseFloat(markPrice.mark_price);
            // Calculate and round to nearest roundingStep
            const calculatedMaxPrice =
              Math.round((markPriceValue * 1.5) / roundingStep) * roundingStep;
            const calculatedMinPrice =
              Math.round(markPriceValue / 1.5 / roundingStep) * roundingStep;
            setMinPrice(calculatedMinPrice);
            setMaxPrice(calculatedMaxPrice);
          }
        }}
        orderMode={orderMode}
        onOrderModeChange={setOrderMode}
        curveIntensity={curveIntensity}
        onCurveIntensityChange={setCurveIntensity}
        roundingStep={roundingStep}
        onRoundingStepChange={setRoundingStep}
      />
    </div>
  );
}
